import { LitElement, html, css, nothing, type PropertyValues } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import "../components/index.js";
import { type BnBadgeVariant, type BnColumn, type BnRow } from "../components/index.js";
import { navigate } from "../nav.js";
import { detailedError, humanError } from "../api/errors.js";
import { getRepository, type RepositoryDetail } from "../api/repositories.js";
import { listTeams, roleAtLeast, type Role } from "../api/teams.js";
import { listCalendars, type Calendar } from "../api/calendars.js";
import {
  listRepositoryDeployments,
  createDeployment,
  discoverAt,
  parseAudience,
  PERIOD_RULES,
  type Deployment,
  type DiscoveredArtefact,
  type PeriodRule,
  type Schedule,
} from "../api/deployments.js";

function formatTime(iso?: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "—" : d.toLocaleString();
}

function statusVariant(status: string): BnBadgeVariant {
  return status === "live" ? "success" : status === "stopped" ? "warning" : "neutral";
}

/** browserZone is the viewer's IANA time zone, the default of a new schedule. */
function browserZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
}

/**
 * page-deployments (/repositories/:id/deployments) lists a repository's
 * deployments and creates drafts. A draft fixes one LiveReportArtefact at one
 * commit, with its period rule, schedule and audience; it sends and serves
 * nothing until a maintainer enables it on its own page.
 *
 * The routes behind the page exist only while the server has deployments on;
 * the first read tells that apart and the page then shows a notice. Finding
 * the artefacts of a commit runs the project indexer in a sandbox, so it runs
 * only when the user asks for it in the create dialog.
 */
@customElement("page-deployments")
export class PageDeployments extends LitElement {
  @property() repositoryId = "";

  @state() private repo: RepositoryDetail | null = null;
  @state() private teamRole: Role = "viewer";
  /** null while deployments are off on the server. */
  @state() private deployments: Deployment[] | null = null;
  @state() private calendars: Calendar[] = [];
  @state() private loading = true;
  @state() private error = "";

  @state() private showCreate = false;
  @state() private branch = "";
  @state() private commit = "";
  @state() private discovering = false;
  @state() private artefacts: DiscoveredArtefact[] | null = null;
  @state() private artefact = "";
  @state() private periodRule: PeriodRule = "previous_month";
  @state() private scheduleKind: "" | "workday" | "cron" = "workday";
  @state() private workday = "3";
  @state() private at = "09:00";
  @state() private cron = "";
  @state() private timezone = browserZone();
  @state() private calendarId = "";
  @state() private windowWorkdays = "3";
  @state() private audience = "";
  @state() private createError = "";
  @state() private creating = false;

  static override styles = css`
    :host {
      display: block;
      font-family: var(--bino-font-sans);
      color: var(--bino-text);
    }
    .back {
      display: inline-flex;
      align-items: center;
      gap: var(--bino-space-xs);
      font-size: var(--bino-font-size-md);
      color: var(--bino-text-secondary);
      text-decoration: none;
      margin-bottom: var(--bino-space-md);
    }
    .back:hover {
      color: var(--bino-text);
    }
    .head {
      display: flex;
      align-items: center;
      gap: var(--bino-space-md);
      margin-bottom: var(--bino-space-lg);
      flex-wrap: wrap;
    }
    h1 {
      margin: 0;
      font-size: var(--bino-font-size-lg);
      font-weight: 600;
    }
    .spacer {
      flex: 1;
    }
    .pad {
      padding: var(--bino-space-md) var(--bino-space-lg);
    }
    .hint {
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
      margin: 0;
    }
    .err {
      font-size: var(--bino-font-size-sm);
      color: var(--bino-error);
      margin: 0 0 var(--bino-space-sm);
    }
    .mono {
      font-family: var(--bino-font-mono);
    }
    .link-btn {
      background: none;
      border: none;
      padding: 0;
      cursor: pointer;
      font-family: var(--bino-font-sans);
      font-size: var(--bino-font-size-base);
      color: var(--bino-primary);
      text-decoration: underline;
    }
    .row {
      display: flex;
      align-items: flex-end;
      gap: var(--bino-space-sm);
      flex-wrap: wrap;
    }
    .row > bn-field {
      flex: 1;
      min-width: 8rem;
    }
    select,
    textarea {
      width: 100%;
      box-sizing: border-box;
      font-family: var(--bino-font-sans);
      font-size: var(--bino-font-size-md);
      color: var(--bino-text);
      padding: 0.5rem 0.6875rem;
      border: 1px solid var(--bino-border-light);
      border-radius: var(--bino-radius);
      background: var(--bino-surface-subtle);
    }
    textarea {
      min-height: 5rem;
      font-family: var(--bino-font-mono);
    }
    select:focus,
    textarea:focus {
      outline: none;
      border-color: var(--bino-primary);
      box-shadow: 0 0 0 3px var(--bino-focus-ring);
    }
  `;

  override updated(changed: PropertyValues) {
    if (changed.has("repositoryId") && this.repositoryId) void this.load();
  }

  private async load() {
    const id = this.repositoryId;
    const stale = () => !this.isConnected || id !== this.repositoryId;
    this.loading = true;
    this.error = "";
    try {
      const [repo, deps, teams] = await Promise.all([
        getRepository(id),
        listRepositoryDeployments(id),
        listTeams().catch(() => []),
      ]);
      if (stale()) return;
      this.repo = repo;
      this.deployments = deps;
      this.teamRole = teams.find((t) => t.id === repo.teamId)?.role ?? "viewer";
      if (deps !== null)
        this.calendars = (await listCalendars(repo.teamId).catch(() => null)) ?? [];
    } catch (e) {
      if (stale()) return;
      this.error = humanError(e);
    } finally {
      if (!stale()) this.loading = false;
    }
  }

  private openCreate() {
    const repo = this.repo;
    if (!repo) return;
    const main = repo.branches.find((b) => b.branch === repo.defaultBranch) ?? repo.branches[0];
    this.branch = main?.branch ?? "";
    this.commit = main?.commit ?? "";
    this.artefacts = null;
    this.artefact = "";
    this.createError = "";
    this.showCreate = true;
  }

  private pickBranch(name: string) {
    this.branch = name;
    this.commit = this.repo?.branches.find((b) => b.branch === name)?.commit ?? "";
    this.artefacts = null;
    this.artefact = "";
  }

  /** discover runs the indexer at the chosen commit, once the user asks. */
  private async discover() {
    this.discovering = true;
    this.createError = "";
    try {
      const found = await discoverAt(
        this.repositoryId,
        this.commit ? { commit: this.commit } : { branch: this.branch },
      );
      this.commit = found.commit;
      this.artefacts = found.items;
      this.artefact = found.items[0]?.artefact ?? "";
    } catch (e) {
      this.createError = detailedError(e);
    } finally {
      this.discovering = false;
    }
  }

  private schedule(): Schedule | undefined {
    switch (this.scheduleKind) {
      case "workday":
        return { kind: "workday", day: Number(this.workday), at: this.at };
      case "cron":
        return { kind: "cron", cron: this.cron.trim() };
      default:
        return undefined;
    }
  }

  private async submitCreate() {
    if (!this.artefact) {
      this.createError = "Find the artefacts of the commit and choose one.";
      return;
    }
    this.creating = true;
    this.createError = "";
    try {
      const d = await createDeployment(this.repositoryId, {
        artefact: this.artefact,
        commit: this.commit,
        periodRule: this.periodRule,
        schedule: this.schedule(),
        timezone: this.timezone.trim(),
        calendarId: this.calendarId || undefined,
        windowWorkdays: Number(this.windowWorkdays),
        audience: parseAudience(this.audience),
      });
      this.showCreate = false;
      navigate(
        `/repositories/${encodeURIComponent(this.repositoryId)}/deployments/${encodeURIComponent(d.id)}`,
      );
    } catch (e) {
      this.createError = detailedError(e);
    } finally {
      this.creating = false;
    }
  }

  override render() {
    if (this.loading && !this.repo) {
      return html`<bn-spinner block label="Loading deployments…"></bn-spinner>`;
    }
    const backHref = `/repositories/${encodeURIComponent(this.repositoryId)}`;
    const back = () => navigate(backHref);
    if (this.error || !this.repo) {
      return html`<bn-empty-state
        icon="⚠"
        heading="Couldn’t load the deployments"
        message=${this.error || "Repository not found."}
      >
        <bn-button slot="actions" variant="secondary" @click=${() => navigate("/workspaces")}
          >Back to workspaces</bn-button
        >
      </bn-empty-state>`;
    }
    if (this.deployments === null) {
      return html`<bn-empty-state
        icon="∅"
        heading="No deployments"
        message="Deployments are not enabled on this server."
      >
        <bn-button slot="actions" variant="secondary" @click=${back}>Back to repository</bn-button>
      </bn-empty-state>`;
    }
    const canCreate = roleAtLeast(this.teamRole, "member");
    return html`
      <a
        class="back"
        href=${backHref}
        @click=${(e: Event) => {
          e.preventDefault();
          back();
        }}
        >← ${this.repo.name}</a
      >
      <div class="head">
        <h1>Deployments</h1>
        <span class="spacer"></span>
        <bn-button variant="ghost" size="sm" @click=${() => this.load()}>Refresh</bn-button>
        ${canCreate
          ? html`<bn-button variant="primary" size="sm" @click=${() => this.openCreate()}
              >New draft</bn-button
            >`
          : nothing}
      </div>
      ${this.renderList(this.deployments)} ${this.renderCreate()}
    `;
  }

  private renderList(deps: Deployment[]) {
    const open = (d: Deployment) =>
      navigate(
        `/repositories/${encodeURIComponent(this.repositoryId)}/deployments/${encodeURIComponent(d.id)}`,
      );
    return html`<bn-card heading="Deployments" flush>
      <div class="pad">
        <p class="hint">
          A deployment shows one artefact at one commit. It makes an edition for each report period,
          on its schedule or when a maintainer sends one, and delivers it through its channels: the
          live URL, a signed link by mail, and a webhook.
        </p>
      </div>
      <bn-table
        .columns=${[
          { key: "artefact", label: "Artefact" },
          { key: "status", label: "Status" },
          { key: "commit", label: "Commit", mono: true },
          { key: "channels", label: "Channels" },
          { key: "rule", label: "Period" },
          { key: "next", label: "Next edition" },
        ] as BnColumn[]}
        .rows=${deps.map(
          (d): BnRow => ({
            artefact: html`<button class="link-btn" @click=${() => open(d)}>${d.artefact}</button>
              ${d.legacy ? html`<bn-badge variant="info">legacy</bn-badge>` : nothing}`,
            status: html`<bn-badge .variant=${statusVariant(d.status)} ?dot=${d.status === "live"}
              >${d.status}</bn-badge
            >`,
            commit: d.commit ? d.commit.slice(0, 8) : "—",
            channels: d.channels?.length ? d.channels.join(", ") : "—",
            rule: PERIOD_RULES.find((r) => r.value === d.periodRule)?.label ?? "—",
            next: formatTime(d.nextDueAt),
          }),
        )}
        empty="No deployments yet. A member creates a draft; a maintainer enables it."
      ></bn-table>
    </bn-card>`;
  }

  private renderCreate() {
    const repo = this.repo;
    return html`<bn-modal
      ?open=${this.showCreate}
      width=${640}
      heading="New draft deployment"
      @close=${() => (this.showCreate = false)}
    >
      ${this.createError ? html`<p class="err">${this.createError}</p>` : nothing}
      <div class="row">
        <bn-field label="Branch">
          <select
            .value=${this.branch}
            @change=${(e: Event) => this.pickBranch((e.target as HTMLSelectElement).value)}
          >
            ${repo?.branches.map(
              (b) =>
                html`<option value=${b.branch} ?selected=${b.branch === this.branch}>
                  ${b.branch}
                </option>`,
            )}
          </select>
        </bn-field>
        <bn-field label="Commit" hint="The head of the branch. The deployment stays on this commit.">
          <bn-input
            mono
            .value=${this.commit}
            @input=${(e: CustomEvent<{ value: string }>) => {
              this.commit = e.detail.value.trim();
              this.artefacts = null;
            }}
          ></bn-input>
        </bn-field>
      </div>
      <bn-field label="Artefact">
        ${this.artefacts === null
          ? html`<bn-button
              variant="secondary"
              size="sm"
              ?loading=${this.discovering}
              @click=${() => this.discover()}
              >Find artefacts at this commit</bn-button
            >`
          : this.artefacts.length === 0
            ? html`<p class="hint">The commit declares no LiveReportArtefact.</p>`
            : html`<select
                .value=${this.artefact}
                @change=${(e: Event) => (this.artefact = (e.target as HTMLSelectElement).value)}
              >
                ${this.artefacts.map(
                  (a) =>
                    html`<option value=${a.artefact} ?selected=${a.artefact === this.artefact}>
                      ${a.artefact}${a.deployments.length
                        ? ` (${a.deployments.length} deployed)`
                        : ""}
                    </option>`,
                )}
              </select>`}
      </bn-field>
      <div class="row">
        <bn-field label="Period rule" hint="The report period each edition covers.">
          <select
            .value=${this.periodRule}
            @change=${(e: Event) =>
              (this.periodRule = (e.target as HTMLSelectElement).value as PeriodRule)}
          >
            ${PERIOD_RULES.map(
              (r) =>
                html`<option value=${r.value} ?selected=${r.value === this.periodRule}>
                  ${r.label}
                </option>`,
            )}
          </select>
        </bn-field>
        <bn-field label="Schedule">
          <select
            .value=${this.scheduleKind}
            @change=${(e: Event) =>
              (this.scheduleKind = (e.target as HTMLSelectElement).value as
                | ""
                | "workday"
                | "cron")}
          >
            <option value="workday" ?selected=${this.scheduleKind === "workday"}>
              Workday of the month
            </option>
            <option value="cron" ?selected=${this.scheduleKind === "cron"}>Cron expression</option>
            <option value="" ?selected=${this.scheduleKind === ""}>None (on demand only)</option>
          </select>
        </bn-field>
      </div>
      ${this.scheduleKind === "workday"
        ? html`<div class="row">
            <bn-field label="Workday" hint="1 is the first workday of the month.">
              <bn-input
                type="number"
                .value=${this.workday}
                @input=${(e: CustomEvent<{ value: string }>) => (this.workday = e.detail.value)}
              ></bn-input>
            </bn-field>
            <bn-field label="At" hint="HH:MM, local time.">
              <bn-input
                mono
                .value=${this.at}
                @input=${(e: CustomEvent<{ value: string }>) => (this.at = e.detail.value)}
              ></bn-input>
            </bn-field>
          </div>`
        : this.scheduleKind === "cron"
          ? html`<bn-field label="Cron" hint="Five fields, local time, e.g. 0 9 * * 1.">
              <bn-input
                mono
                .value=${this.cron}
                @input=${(e: CustomEvent<{ value: string }>) => (this.cron = e.detail.value)}
              ></bn-input>
            </bn-field>`
          : nothing}
      <div class="row">
        <bn-field label="Time zone" hint="IANA name, e.g. Europe/Berlin.">
          <bn-input
            mono
            .value=${this.timezone}
            @input=${(e: CustomEvent<{ value: string }>) => (this.timezone = e.detail.value)}
          ></bn-input>
        </bn-field>
        <bn-field label="Calendar">
          <select
            .value=${this.calendarId}
            @change=${(e: Event) => (this.calendarId = (e.target as HTMLSelectElement).value)}
          >
            <option value="" ?selected=${this.calendarId === ""}>Monday to Friday</option>
            ${this.calendars.map(
              (c) =>
                html`<option value=${c.id} ?selected=${c.id === this.calendarId}>
                  ${c.name}
                </option>`,
            )}
          </select>
        </bn-field>
        <bn-field label="Window" hint="Workdays an edition waits for its seals.">
          <bn-input
            type="number"
            .value=${this.windowWorkdays}
            @input=${(e: CustomEvent<{ value: string }>) => (this.windowWorkdays = e.detail.value)}
          ></bn-input>
        </bn-field>
      </div>
      <bn-field label="Mail audience" hint="Optional. One address per line, at most 50.">
        <textarea
          .value=${this.audience}
          spellcheck="false"
          @input=${(e: Event) => (this.audience = (e.target as HTMLTextAreaElement).value)}
        ></textarea>
      </bn-field>
      <p class="hint">
        A draft sends and serves nothing. A maintainer enables it with the channels it should have.
      </p>
      <bn-button slot="footer" variant="ghost" @click=${() => (this.showCreate = false)}
        >Cancel</bn-button
      >
      <bn-button
        slot="footer"
        variant="primary"
        ?loading=${this.creating}
        ?disabled=${!this.artefact}
        @click=${() => this.submitCreate()}
        >Create draft</bn-button
      >
    </bn-modal>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "page-deployments": PageDeployments;
  }
}
